import React, { useState, useMemo } from 'react';
import { Icons } from './Icons';

function MonthlyStatsModal({ salesData, showModal, onClose }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('overview'); // overview, monthly, payment, location, seller

  const availableYears = useMemo(() => {
    const years = new Set();
    salesData.forEach(sale => years.add(sale.saleDate.getFullYear()));
    return Array.from(years).sort((a, b) => b - a);
  }, [salesData]);

  // 선택된 연도의 데이터만 필터링
  const yearData = useMemo(() => {
    return salesData.filter(sale => sale.saleDate.getFullYear() === selectedYear);
  }, [salesData, selectedYear]);

  // 연간 요약 통계
  const annualSummary = useMemo(() => {
    const summary = {
      totalTransaction: 0,
      totalSales: 0,
      totalCost: 0,
      totalFees: 0,
      totalProfit: 0,
      salesCount: 0,
      avgTransaction: 0,
      avgProfit: 0,
      profitRate: 0,
    };

    yearData.forEach(sale => {
      summary.totalTransaction += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
      summary.totalSales += sale.salesTotal || sale.salesBaseAmount || 0;
      summary.totalCost += sale.purchasePriceAtSale || sale.purchasePrice || 0;
      summary.totalFees += sale.totalFees || sale.cardFee || 0;
      summary.totalProfit += sale.netProfit || sale.profit || 0;
      summary.salesCount += 1;
    });

    if (summary.salesCount > 0) {
      summary.avgTransaction = summary.totalTransaction / summary.salesCount;
      summary.avgProfit = summary.totalProfit / summary.salesCount;
    }
    if (summary.totalSales > 0) {
      summary.profitRate = (summary.totalProfit / summary.totalSales) * 100;
    }

    return summary;
  }, [yearData]);

  // 월별 통계 (기존 + 확장)
  const monthlyStats = useMemo(() => {
    const stats = {};

    yearData.forEach(sale => {
      const month = sale.saleDate.getMonth() + 1;
      const key = `${selectedYear}-${String(month).padStart(2, '0')}`;

      if (!stats[key]) {
        stats[key] = {
          month,
          totalTransaction: 0,
          totalSales: 0,
          totalCost: 0,
          totalFees: 0,
          totalProfit: 0,
          salesCount: 0,
        };
      }

      stats[key].totalTransaction += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
      stats[key].totalSales += sale.salesTotal || sale.salesBaseAmount || 0;
      stats[key].totalCost += sale.purchasePriceAtSale || sale.purchasePrice || 0;
      stats[key].totalFees += sale.totalFees || sale.cardFee || 0;
      stats[key].totalProfit += sale.netProfit || sale.profit || 0;
      stats[key].salesCount += 1;
    });

    // 수익률 계산 및 전월 대비 변동 추가
    const sortedStats = Object.entries(stats).sort(([a], [b]) => a.localeCompare(b));
    return sortedStats.map(([key, data], index) => {
      const profitRate = data.totalSales > 0 ? (data.totalProfit / data.totalSales) * 100 : 0;
      const prevMonth = index > 0 ? sortedStats[index - 1][1] : null;
      const transactionChange = prevMonth
        ? ((data.totalTransaction - prevMonth.totalTransaction) / prevMonth.totalTransaction * 100)
        : null;
      const profitChange = prevMonth
        ? ((data.totalProfit - prevMonth.totalProfit) / (prevMonth.totalProfit || 1) * 100)
        : null;

      return {
        key,
        ...data,
        profitRate,
        transactionChange,
        profitChange,
      };
    });
  }, [yearData, selectedYear]);

  // 결제 수단별 통계
  const paymentMethodStats = useMemo(() => {
    const stats = {};

    yearData.forEach(sale => {
      const method = sale.paymentMethod || '기타';

      if (!stats[method]) {
        stats[method] = {
          method,
          totalTransaction: 0,
          totalFees: 0,
          salesCount: 0,
        };
      }

      stats[method].totalTransaction += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
      stats[method].totalFees += sale.totalFees || sale.cardFee || 0;
      stats[method].salesCount += 1;
    });

    return Object.values(stats).sort((a, b) => b.totalTransaction - a.totalTransaction);
  }, [yearData]);

  // 장소별 통계
  const locationStats = useMemo(() => {
    const stats = {};

    yearData.forEach(sale => {
      const location = sale.saleLocation || '기타';

      if (!stats[location]) {
        stats[location] = {
          location,
          totalTransaction: 0,
          totalProfit: 0,
          salesCount: 0,
        };
      }

      stats[location].totalTransaction += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
      stats[location].totalProfit += sale.netProfit || sale.profit || 0;
      stats[location].salesCount += 1;
    });

    return Object.values(stats).sort((a, b) => b.totalTransaction - a.totalTransaction);
  }, [yearData]);

  // 소유 구분별 통계 (직판/위탁)
  const ownershipStats = useMemo(() => {
    const stats = { '직판': { count: 0, transaction: 0, profit: 0 }, '위탁': { count: 0, transaction: 0, profit: 0 } };

    yearData.forEach(sale => {
      const type = sale.ownershipType === '위탁' ? '위탁' : '직판';
      stats[type].count += 1;
      stats[type].transaction += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
      stats[type].profit += sale.netProfit || sale.profit || 0;
    });

    return stats;
  }, [yearData]);

  // 판매자별 통계
  const sellerStats = useMemo(() => {
    const stats = {};

    yearData.forEach(sale => {
      const seller = sale.seller || '미지정';
      const displayName = seller === 'leehoins@gmail.com' ? '이호인'
        : seller === 'oneman0318@naver.com' ? '이찬주'
        : seller;

      if (!stats[displayName]) {
        stats[displayName] = {
          seller: displayName,
          totalTransaction: 0,
          totalProfit: 0,
          salesCount: 0,
        };
      }

      stats[displayName].totalTransaction += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
      stats[displayName].totalProfit += sale.netProfit || sale.profit || 0;
      stats[displayName].salesCount += 1;
    });

    return Object.values(stats).sort((a, b) => b.totalTransaction - a.totalTransaction);
  }, [yearData]);

  // 수금 상태별 통계
  const collectionStats = useMemo(() => {
    const stats = { '수금완료': { count: 0, amount: 0 }, '미수금': { count: 0, amount: 0 } };

    yearData.forEach(sale => {
      const status = sale.collectionStatus === '완료' ? '수금완료' : '미수금';
      stats[status].count += 1;
      stats[status].amount += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
    });

    return stats;
  }, [yearData]);

  // 배송 상태별 통계
  const shippingStats = useMemo(() => {
    const stats = {};

    yearData.forEach(sale => {
      const status = sale.shippingStatus || '배송없음';

      if (!stats[status]) {
        stats[status] = { status, count: 0, amount: 0 };
      }

      stats[status].count += 1;
      stats[status].amount += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
    });

    return Object.values(stats).sort((a, b) => b.count - a.count);
  }, [yearData]);

  // 세금계산서 발행 통계
  const taxInvoiceStats = useMemo(() => {
    const stats = { '발행': { count: 0, amount: 0 }, '미발행': { count: 0, amount: 0 } };

    yearData.forEach(sale => {
      const status = sale.taxInvoice ? '발행' : '미발행';
      stats[status].count += 1;
      stats[status].amount += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
    });

    return stats;
  }, [yearData]);

  // 카드사별 통계
  const cardCompanyStats = useMemo(() => {
    const stats = {};

    yearData.forEach(sale => {
      if (sale.paymentMethod === '카드' && sale.cardCompany) {
        const company = sale.cardCompany;

        if (!stats[company]) {
          stats[company] = {
            company,
            totalTransaction: 0,
            totalFees: 0,
            salesCount: 0,
          };
        }

        stats[company].totalTransaction += sale.transactionTotal || sale.total || sale.actualSalePrice || 0;
        stats[company].totalFees += sale.cardFee || 0;
        stats[company].salesCount += 1;
      }
    });

    return Object.values(stats).sort((a, b) => b.totalTransaction - a.totalTransaction);
  }, [yearData]);

  if (!showModal) return null;

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('ko-KR').format(Math.round(price));
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const tabs = [
    { id: 'overview', label: '요약', icon: Icons.ChartBar },
    { id: 'monthly', label: '월별', icon: Icons.Calendar },
    { id: 'payment', label: '결제', icon: Icons.CreditCard },
    { id: 'location', label: '장소', icon: Icons.MapPin },
    { id: 'seller', label: '판매자', icon: Icons.Users },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-brand-900 text-white px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Icons.ChartBar className="w-5 h-5" />
            월별 매출 통계
          </h3>
          <button onClick={onClose} className="text-white hover:text-gray-300">
            <Icons.Close className="w-6 h-6" />
          </button>
        </div>

        {/* Year selector & Tabs */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">연도:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}년</option>
                ))}
              </select>
              <span className="text-sm text-gray-500 ml-2">
                총 {yearData.length}건
              </span>
            </div>

            <div className="flex gap-1 bg-gray-200 p-1 rounded-lg">
              {tabs.map(tab => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 transition-colors ${
                      activeTab === tab.id
                        ? 'bg-white text-brand-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {yearData.length === 0 ? (
            <div className="text-center py-16">
              <Icons.ChartBar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">선택된 연도에 대한 매출 데이터가 없습니다.</p>
            </div>
          ) : (
            <>
              {/* 요약 탭 */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* 연간 요약 카드 */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-xs text-blue-600 font-medium">총 거래액</div>
                      <div className="text-xl font-bold text-blue-900 mt-1">{formatPrice(annualSummary.totalTransaction)}원</div>
                      <div className="text-xs text-blue-500 mt-1">{annualSummary.salesCount}건</div>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-4">
                      <div className="text-xs text-indigo-600 font-medium">총 매출액</div>
                      <div className="text-xl font-bold text-indigo-900 mt-1">{formatPrice(annualSummary.totalSales)}원</div>
                      <div className="text-xs text-indigo-500 mt-1">평균 {formatPrice(annualSummary.avgTransaction)}원</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-xs text-green-600 font-medium">총 순수익</div>
                      <div className={`text-xl font-bold mt-1 ${annualSummary.totalProfit >= 0 ? 'text-green-900' : 'text-red-600'}`}>
                        {formatPrice(annualSummary.totalProfit)}원
                      </div>
                      <div className="text-xs text-green-500 mt-1">수익률 {annualSummary.profitRate.toFixed(1)}%</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-xs text-orange-600 font-medium">총 수수료</div>
                      <div className="text-xl font-bold text-orange-900 mt-1">{formatPrice(annualSummary.totalFees)}원</div>
                      <div className="text-xs text-orange-500 mt-1">원가 {formatPrice(annualSummary.totalCost)}원</div>
                    </div>
                  </div>

                  {/* 소유 구분별 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <Icons.Tag className="w-4 h-4" />
                        소유 구분별 통계
                      </h4>
                      <div className="space-y-3">
                        {Object.entries(ownershipStats).map(([type, data]) => (
                          <div key={type} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                type === '직판' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {type}
                              </span>
                              <span className="text-sm text-gray-600">{data.count}건</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">{formatPrice(data.transaction)}원</div>
                              <div className={`text-xs ${data.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                순수익 {formatPrice(data.profit)}원
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 수금/배송 현황 */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <Icons.Check className="w-4 h-4" />
                        수금/세금계산서 현황
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">수금완료</span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-green-600">{collectionStats['수금완료'].count}건</span>
                            <span className="text-xs text-gray-400 ml-2">({formatPrice(collectionStats['수금완료'].amount)}원)</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">미수금</span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-red-600">{collectionStats['미수금'].count}건</span>
                            <span className="text-xs text-gray-400 ml-2">({formatPrice(collectionStats['미수금'].amount)}원)</span>
                          </div>
                        </div>
                        <hr className="my-2" />
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">세금계산서 발행</span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-blue-600">{taxInvoiceStats['발행'].count}건</span>
                            <span className="text-xs text-gray-400 ml-2">({formatPrice(taxInvoiceStats['발행'].amount)}원)</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">세금계산서 미발행</span>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-500">{taxInvoiceStats['미발행'].count}건</span>
                            <span className="text-xs text-gray-400 ml-2">({formatPrice(taxInvoiceStats['미발행'].amount)}원)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 배송 상태별 */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <Icons.Truck className="w-4 h-4" />
                      배송 상태별 현황
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {shippingStats.map(stat => (
                        <div key={stat.status} className="bg-gray-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-gray-900">{stat.count}건</div>
                          <div className="text-xs text-gray-500">{stat.status}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 월별 탭 */}
              {activeTab === 'monthly' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">월</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">건수</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">거래액</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">매출액</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">원가</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">수수료</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">순수익</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">수익률</th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">전월대비</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {monthlyStats.map((data) => (
                        <tr key={data.key} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-center font-medium text-gray-900">{data.month}월</td>
                          <td className="px-3 py-2 text-center text-gray-700">{data.salesCount}건</td>
                          <td className="px-3 py-2 text-center text-blue-600 font-medium">{formatPrice(data.totalTransaction)}</td>
                          <td className="px-3 py-2 text-center text-indigo-600">{formatPrice(data.totalSales)}</td>
                          <td className="px-3 py-2 text-center text-gray-600">{formatPrice(data.totalCost)}</td>
                          <td className="px-3 py-2 text-center text-orange-600">{formatPrice(data.totalFees)}</td>
                          <td className={`px-3 py-2 text-center font-medium ${data.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPrice(data.totalProfit)}
                          </td>
                          <td className={`px-3 py-2 text-center ${data.profitRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {data.profitRate.toFixed(1)}%
                          </td>
                          <td className="px-3 py-2 text-center">
                            {data.transactionChange !== null ? (
                              <span className={data.transactionChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {formatPercent(data.transactionChange)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 font-bold">
                      <tr>
                        <td className="px-3 py-2 text-center">합계</td>
                        <td className="px-3 py-2 text-center">{annualSummary.salesCount}건</td>
                        <td className="px-3 py-2 text-center text-blue-700">{formatPrice(annualSummary.totalTransaction)}</td>
                        <td className="px-3 py-2 text-center text-indigo-700">{formatPrice(annualSummary.totalSales)}</td>
                        <td className="px-3 py-2 text-center text-gray-700">{formatPrice(annualSummary.totalCost)}</td>
                        <td className="px-3 py-2 text-center text-orange-700">{formatPrice(annualSummary.totalFees)}</td>
                        <td className={`px-3 py-2 text-center ${annualSummary.totalProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatPrice(annualSummary.totalProfit)}
                        </td>
                        <td className={`px-3 py-2 text-center ${annualSummary.profitRate >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {annualSummary.profitRate.toFixed(1)}%
                        </td>
                        <td className="px-3 py-2 text-center">-</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* 결제 탭 */}
              {activeTab === 'payment' && (
                <div className="space-y-6">
                  {/* 결제수단별 */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 mb-3">결제 수단별 통계</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            <th className="px-4 py-2 text-left text-xs font-bold text-gray-600">결제수단</th>
                            <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">건수</th>
                            <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">거래액</th>
                            <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">비중</th>
                            <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">수수료</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {paymentMethodStats.map((stat) => (
                            <tr key={stat.method} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-gray-900">{stat.method}</td>
                              <td className="px-4 py-2 text-center text-gray-700">{stat.salesCount}건</td>
                              <td className="px-4 py-2 text-center text-blue-600 font-medium">{formatPrice(stat.totalTransaction)}원</td>
                              <td className="px-4 py-2 text-center text-gray-600">
                                {((stat.totalTransaction / annualSummary.totalTransaction) * 100).toFixed(1)}%
                              </td>
                              <td className="px-4 py-2 text-center text-orange-600">{formatPrice(stat.totalFees)}원</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* 카드사별 */}
                  {cardCompanyStats.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3">카드사별 통계</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200 text-sm">
                          <thead>
                            <tr className="bg-gray-50 border-b">
                              <th className="px-4 py-2 text-left text-xs font-bold text-gray-600">카드사</th>
                              <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">건수</th>
                              <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">거래액</th>
                              <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">수수료</th>
                              <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">수수료율</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {cardCompanyStats.map((stat) => (
                              <tr key={stat.company} className="hover:bg-gray-50">
                                <td className="px-4 py-2 font-medium text-gray-900">{stat.company}</td>
                                <td className="px-4 py-2 text-center text-gray-700">{stat.salesCount}건</td>
                                <td className="px-4 py-2 text-center text-blue-600 font-medium">{formatPrice(stat.totalTransaction)}원</td>
                                <td className="px-4 py-2 text-center text-orange-600">{formatPrice(stat.totalFees)}원</td>
                                <td className="px-4 py-2 text-center text-gray-600">
                                  {stat.totalTransaction > 0 ? ((stat.totalFees / stat.totalTransaction) * 100).toFixed(2) : 0}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 장소 탭 */}
              {activeTab === 'location' && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-3">판매 장소별 통계</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200 text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="px-4 py-2 text-left text-xs font-bold text-gray-600">장소</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">건수</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">거래액</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">비중</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">순수익</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">평균 수익</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {locationStats.map((stat) => (
                          <tr key={stat.location} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">{stat.location}</td>
                            <td className="px-4 py-2 text-center text-gray-700">{stat.salesCount}건</td>
                            <td className="px-4 py-2 text-center text-blue-600 font-medium">{formatPrice(stat.totalTransaction)}원</td>
                            <td className="px-4 py-2 text-center text-gray-600">
                              {((stat.totalTransaction / annualSummary.totalTransaction) * 100).toFixed(1)}%
                            </td>
                            <td className={`px-4 py-2 text-center font-medium ${stat.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPrice(stat.totalProfit)}원
                            </td>
                            <td className="px-4 py-2 text-center text-gray-600">
                              {formatPrice(stat.totalProfit / stat.salesCount)}원
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 판매자 탭 */}
              {activeTab === 'seller' && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-3">판매자별 실적</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200 text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="px-4 py-2 text-left text-xs font-bold text-gray-600">판매자</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">건수</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">거래액</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">비중</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">순수익</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-gray-600">건당 평균</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {sellerStats.map((stat) => (
                          <tr key={stat.seller} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">{stat.seller}</td>
                            <td className="px-4 py-2 text-center text-gray-700">{stat.salesCount}건</td>
                            <td className="px-4 py-2 text-center text-blue-600 font-medium">{formatPrice(stat.totalTransaction)}원</td>
                            <td className="px-4 py-2 text-center text-gray-600">
                              {((stat.totalTransaction / annualSummary.totalTransaction) * 100).toFixed(1)}%
                            </td>
                            <td className={`px-4 py-2 text-center font-medium ${stat.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPrice(stat.totalProfit)}원
                            </td>
                            <td className="px-4 py-2 text-center text-gray-600">
                              {formatPrice(stat.totalTransaction / stat.salesCount)}원
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

export default MonthlyStatsModal;
